import React, { useEffect, useState } from "react";
import { Loader2, Save, CheckCircle2, Upload, Trash2 } from "lucide-react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import MediaPicker from "./MediaPicker";

/**
 * Hero background image chooser — moved out of the Homepage widgets
 * page so admins can edit it directly from the main Admin dashboard,
 * placed between the section pills (categories) and the posts list
 * (blog posts).
 *
 * Persists to /api/settings/homepage-hero-bg.
 */
async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const r = await fetch(`${API}/uploads`, {
    method: "POST",
    credentials: "include",
    body: fd,
  });
  if (!r.ok) throw new Error("Upload failed");
  const data = await r.json();
  return data.url;
}

export default function AdminHeroBgPanel() {
  const [heroBg, setHeroBg] = useState({ image_url: "", opacity: 25 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/settings/homepage-hero-bg")
      .then((r) => setHeroBg(r.data || { image_url: "", opacity: 25 }))
      .catch((e) => setErr(formatApiErrorDetail(e)))
      .finally(() => setLoading(false));
  }, []);

  const onFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const url = await uploadImage(file);
      setHeroBg((s) => ({ ...s, image_url: url }));
    } catch (_e) {
      setErr("Hero background upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.put("/settings/homepage-hero-bg", heroBg);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2000);
    } catch (e) {
      setErr(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section
        className="mb-8 rounded-md border border-black/10 bg-white p-5"
        data-testid="admin-hero-bg-panel"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </section>
    );
  }

  return (
    <section
      className="mb-8 rounded-md border border-black/10 bg-white p-5"
      data-testid="admin-hero-bg-panel"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Hero background image</h2>
          <p className="mt-1 text-xs text-[var(--brand-body)]">
            Painted underneath the home page hero card and right-column
            widgets. Adjust opacity so titles and copy stay readable.
            Leave the URL blank to remove.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {savedFlash ? (
            <span
              className="inline-flex items-center gap-1 text-sm text-emerald-700"
              data-testid="admin-hero-bg-saved-flash"
            >
              <CheckCircle2 className="h-4 w-4" /> Saved
            </span>
          ) : null}
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-3 py-2 font-display text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
            data-testid="admin-hero-bg-save"
          >
            {saving ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Save className="h-3 w-3" />
            )}
            Save hero background
          </button>
        </div>
      </div>

      {err ? (
        <div
          className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700"
          data-testid="admin-hero-bg-error"
        >
          {err}
        </div>
      ) : null}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
              Image URL
            </label>
            <input
              type="text"
              value={heroBg.image_url || ""}
              onChange={(e) => setHeroBg({ ...heroBg, image_url: e.target.value })}
              placeholder="/api/files/... or https://"
              className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              data-testid="admin-hero-bg-url"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-black/15 px-3 py-1.5 text-xs font-semibold hover:border-[#0A0F17]">
                {uploading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Upload className="h-3 w-3" />
                )}
                Upload hero background
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files && e.target.files[0])}
                  data-testid="admin-hero-bg-upload"
                />
              </label>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                data-testid="admin-hero-bg-from-library"
                className="inline-flex items-center gap-1 text-xs text-[var(--brand-body)] underline-offset-2 hover:text-[var(--brand-ink)] hover:underline"
              >
                …or pick from library
              </button>
              {heroBg.image_url ? (
                <button
                  type="button"
                  onClick={() => setHeroBg({ ...heroBg, image_url: "" })}
                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                  data-testid="admin-hero-bg-clear"
                >
                  <Trash2 className="h-3 w-3" /> Clear
                </button>
              ) : null}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--brand-body)]">
              Image opacity ({heroBg.opacity ?? 25}%)
            </label>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={heroBg.opacity ?? 25}
              onChange={(e) =>
                setHeroBg({ ...heroBg, opacity: Number(e.target.value) })
              }
              className="mt-2 w-full"
              data-testid="admin-hero-bg-opacity"
            />
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              0% = invisible · 25% = subtle wash (recommended) · 100% = full image
            </p>
          </div>
        </div>

        <div>
          <p className="label-kicker text-[var(--brand-body)]">Preview</p>
          <div
            className="mt-2 relative flex h-40 items-center justify-center overflow-hidden rounded-md border border-black/10 bg-white text-sm"
            data-testid="admin-hero-bg-preview"
          >
            {heroBg.image_url ? (
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${heroBg.image_url})`,
                  opacity: Math.max(0, Math.min(1, (Number(heroBg.opacity) || 0) / 100)),
                }}
              />
            ) : null}
            <span className="relative z-10 font-display text-base font-bold">
              Sample hero title
            </span>
          </div>
        </div>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => {
          setHeroBg({ ...heroBg, image_url: url });
          setPickerOpen(false);
        }}
        accept="image"
      />
    </section>
  );
}
